import React from 'react';
import { Link } from 'react-router-dom';
import AzulejoPanel from '../components/AzulejoPanel';
import WatercolorWash from '../components/WatercolorWash';
import SEO from '../components/SEO';
import { usePets } from '../hooks/usePets';
import '../styles/AdotarChooser.css';

interface ChoiceCardProps {
  to: string;
  chapter: string;
  title: string;
  count: number | null;
  blurb: string;
  cta: string;
  tilesAccent: 'dog' | 'cat';
}

const ChoiceCard: React.FC<ChoiceCardProps> = ({
  to, chapter, title, count, blurb, cta, tilesAccent,
}) => (
  <Link to={to} className={`choice-card choice-card--${tilesAccent}`}>
    <div className="choice-card__azulejo">
      <AzulejoPanel
        stamp={null}
        tiles={
          tilesAccent === 'dog'
            ? ['quatrefoil', 'compass', 'leaf',
               'crossquare', 'dotcorners', 'cushion',
               'compass', 'quatrefoil', 'crossquare',
               'leaf', 'cushion', 'dotcorners']
            : ['leaf', 'cushion', 'crossquare',
               'compass', 'quatrefoil', 'dotcorners',
               'cushion', 'leaf', 'compass',
               'crossquare', 'dotcorners', 'quatrefoil']
        }
      />
    </div>
    <div className="choice-card__copy">
      <div className="choice-card__chapter">{chapter}</div>
      <h2 className="choice-card__title">{title}</h2>
      <p className="choice-card__blurb">{blurb}</p>
      <div className="choice-card__count">
        {count == null
          ? <span className="choice-card__count-placeholder">à espera de contagem…</span>
          : <>
              <strong>{count}</strong>{' '}
              {count === 1 ? 'à espera' : 'à espera'}
            </>}
      </div>
      <span className="choice-card__cta">{cta} →</span>
    </div>
  </Link>
);

const AdotarChooser: React.FC = () => {
  const dogs = usePets({ type: 'DOG', size: 1 });
  const cats = usePets({ type: 'CAT', size: 1 });

  const dogCount = dogs.loading ? null : dogs.totalElements;
  const catCount = cats.loading ? null : cats.totalElements;

  return (
    <div className="chooser">
      <SEO
        title="Animais para Adoção | Patinhas Sem Lar"
        description="Conheça cães e gatos disponíveis para adoção responsável na zona do Porto."
        path="/adotar"
      />
      <WatercolorWash variant="b" color="#c68c3b" style={{ top: '-60px', left: '-80px' }} />

      <header className="chooser__header">
        <div className="chooser__chapter">Cap. II · Adotar</div>
        <h1 className="chooser__title">
          Quem queres <em>conhecer</em><br />
          primeiro?
        </h1>
        <p className="chooser__subtitle">
          Dois cadernos paralelos: cães e gatos, cada um com a sua rotina.
          Escolhe um para começar — podes voltar e folhear o outro a seguir.
        </p>
      </header>

      <div className="chooser__cards">
        <ChoiceCard
          to="/adotar/caes"
          chapter="— os de quatro patas grandes"
          title="Cães"
          count={dogCount}
          blurb="Pequenos, médios, gigantes. Calmos, brincalhões, recém-chegados, à espera há tempo."
          cta="Conhecer os cães"
          tilesAccent="dog"
        />
        <ChoiceCard
          to="/adotar/gatos"
          chapter="— os de quatro patas pequenos"
          title="Gatos"
          count={catCount}
          blurb="Curiosos e independentes, faladores e silenciosos. Cada um com a sua janela favorita."
          cta="Conhecer os gatos"
          tilesAccent="cat"
        />
      </div>

      <p className="chooser__small-print">
        Não tens a certeza? <Link to="/">Volta à página inicial</Link> e folheia
        as fichas em destaque — ou liga-nos primeiro.
      </p>
    </div>
  );
};

export default AdotarChooser;
